import { useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import { Button, Chip, typographyVariants } from '@mint-lib/tw-components';
import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteInput,
  AutocompleteTrigger,
} from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import type { CustomMetric, GranularityLevel } from '../../api/types.js';
import {
  useCustomMetricsTypesLabelsByName,
  useLocalizedMetricTypesLabels,
} from '../../constants/index.js';
import { useCustomMetricsDrawer } from '../../contexts/CustomMetricsContext.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';

export type HeaderOption = {
  name: string;
  label: string;
  headerType: 'dimension' | 'metric' | 'custom_metric' | 'noCustomMetrics';
  disabled?: boolean;
  required?: boolean;
  metricType?: CustomMetric['metricType'];
};

type MetricAutocompleteProps<Value extends HeaderOption> = {
  defaultValue?: Value | null;
  disabled?: boolean;
  disableClearable?: boolean;
  maxMetricsCount: number;
  onChange: (_: unknown, value: Value | null) => void;
  options: Value[];
  isMetricsLimitReached: boolean;
  granularityLevel: GranularityLevel;
  value?: Value | null;
};

const renderMetricOptionExtra = <Value extends HeaderOption>(
  option: Value,
  customMetricsTypesLabelsByName: ReturnType<
    typeof useCustomMetricsTypesLabelsByName
  >,
) => (
  <>
    {option.required && <span className="mr-auto -ml-1">*</span>}
    {option.metricType && (
      <Chip variant="outline" size="small" color="silver">
        {customMetricsTypesLabelsByName[option.metricType]}
      </Chip>
    )}
  </>
);

export const MetricAutocomplete = <Value extends HeaderOption>({
  defaultValue,
  disabled,
  disableClearable,
  onChange,
  options,
  granularityLevel,
  value,
}: MetricAutocompleteProps<Value>) => {
  const { t } = useTranslation('@myn/import-hub');

  const customMetricsTypesLabelsByName = useCustomMetricsTypesLabelsByName();
  const metricTypes = useLocalizedMetricTypesLabels();
  const selectedOption = value ?? defaultValue ?? null;
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLocaleLowerCase();

    if (!normalizedSearch) {
      return options;
    }

    return options.filter((option) => {
      if (option.headerType === 'noCustomMetrics') {
        return true;
      }

      return option.label.toLocaleLowerCase().includes(normalizedSearch);
    });
  }, [options, searchValue]);

  const handleClear = () => {
    setSearchValue('');
    onChange(null, null);
  };

  const clearProps = disableClearable
    ? ({ clearable: false } as const)
    : ({ clearable: true, onClear: handleClear } as const);

  return (
    <div className="w-full">
      <Autocomplete<Value>
        key={selectedOption?.name ?? 'empty'}
        options={filteredOptions}
        size="small"
        autoFocus={false}
        disabled={disabled}
        defaultValue={selectedOption?.label ?? ''}
        getOptionLabel={(option) => option.label ?? ''}
        isSelectedOption={(option) => option.name === selectedOption?.name}
        onAutocompleteChange={(option) => {
          if (option.headerType === 'noCustomMetrics' || option.disabled) {
            return;
          }

          setSearchValue('');
          onChange(null, option);
        }}
        formatAutocompleteValue={(value) => value}
        {...clearProps}
      >
        <AutocompleteTrigger fullWidth>
          <AutocompleteInput<Value | null>
            placeholder={t('Select')}
            selectedOption={selectedOption}
            onAutocompleteValueChange={setSearchValue}
            autoComplete="off"
          />
        </AutocompleteTrigger>
        <AutocompleteContent<Value>
          noOptionsText={t('No options')}
          groupBy={(option) => option.headerType}
          getOptionHidden={(option) => option.headerType === 'noCustomMetrics'}
          renderGroupHeader={(group, groupOptions) => {
            const hasOptions = groupOptions.some(
              (option) => option.headerType !== 'noCustomMetrics',
            );

            return (
              <MetricsGroup
                group={group as Value['headerType']}
                metricTypes={metricTypes}
                hasOptions={hasOptions}
                granularityLevel={granularityLevel}
              />
            );
          }}
          getOptionExtra={(option) =>
            renderMetricOptionExtra(option, customMetricsTypesLabelsByName)
          }
        />
      </Autocomplete>
    </div>
  );
};

type MetricsGroupProps<Value extends HeaderOption> = {
  group: Value['headerType'];
  metricTypes: Record<Value['headerType'], string>;
  hasOptions: boolean;
  granularityLevel: GranularityLevel;
};

const MetricsGroup = <Value extends HeaderOption>({
  group,
  metricTypes,
  hasOptions,
  granularityLevel,
}: MetricsGroupProps<Value>) => {
  const { t } = useTranslation('@myn/import-hub');
  const { openDrawer } = useCustomMetricsDrawer();

  const isPricingItemLevel = granularityLevel === 'pricing_item';

  const isSystemMetricsGroup = group === 'metric';
  const isCustomMetricsGroup = ['custom_metric', 'noCustomMetrics'].includes(
    group,
  );

  const isRequiredGroup =
    (isPricingItemLevel && isSystemMetricsGroup) ||
    (!isPricingItemLevel && isCustomMetricsGroup);

  return (
    <div className="px-3 py-4">
      <div className="flex items-center">
        <span className={typographyVariants({ variant: 'overline1' })}>
          {metricTypes[group]}
        </span>
        {['metric', 'custom_metric', 'noCustomMetrics'].includes(group) && (
          <>
            {isRequiredGroup && (
              <>
                <Tooltip
                  asChild
                  description={
                    isSystemMetricsGroup
                      ? t(
                          'Enhance your data analysis by exploring campaign Reporting through our database metrics.',
                        )
                      : t(
                          'Create and manage custom metrics to tailor data tracking to your specific needs.',
                        )
                  }
                >
                  <div className="mr-1 ml-2 inline-flex">
                    <Information className="fill-icon-secondary" />
                  </div>
                </Tooltip>
                <span
                  className={cn(
                    typographyVariants({ variant: 'label01' }),
                    'normal-case',
                  )}
                >
                  {t('Choose at least one metric')}
                </span>
              </>
            )}
            {isCustomMetricsGroup && (
              <Button
                onClick={openDrawer}
                variant="plain"
                size="large"
                className="ml-auto"
                data-dd-action-name={'Manage custom metrics'}
              >
                {hasOptions ? t('Manage') : t('Create custom metric')}
              </Button>
            )}
          </>
        )}
      </div>
      {isCustomMetricsGroup && !hasOptions && (
        <div className="pt-4">
          <span className={typographyVariants({ variant: 'label01' })}>
            {t('No custom metrics available yet. Add some to proceed.')}
          </span>
        </div>
      )}
    </div>
  );
};
